import {
  ENVIRONMENTS,
  SearchPanelConfig,
  assetPath,
  currentEnvironment,
} from './ssr-helper';
import { getHostName } from './basepaths';
import { Logger } from './services/logging';

export enum BRAND_NAMES {
  VAA = 'Virgin Atlantic',
}

type PreloadFile = {
  name: string;
  path: string;
  type: string;
  ext: string;
};

export interface PageHeader {
  title: string;
  description: string | null;
  canonicalUrl: string;
  brandName: string;
  favicon: string;
  preloadFiles?: PreloadFile[];
  translationsJS?: {
    id: string;
    url: string;
  };
  faviconAppleTouch: string;
  favicon16: string;
  favicon32: string;
  faviconSafariPinned: string;
  appleTouchIconPrecomposed: string;
  searchPanelConfig?: SearchPanelConfig;
  isHomepage?: boolean;
  localeInfo?: string;
  aemLastModifiedDate?: string;
  regenerateTime?: string;
}

export const getEnsightenBootstrapBasepath = () => {
  switch (currentEnvironment) {
    case ENVIRONMENTS.TEST1:
    case ENVIRONMENTS.TEST2:
    case ENVIRONMENTS.TEST3:
    case ENVIRONMENTS.TEST4:
    case ENVIRONMENTS.TEST5:
    case ENVIRONMENTS.UAT:
    case ENVIRONMENTS.LOCAL:
      return 'https://tms.virginatlantic.com/vaa/ue_bw_stg/Bootstrap.js';
    case ENVIRONMENTS.PROD:
      return 'https://tms.virginatlantic.com/vaa/ue_bw/Bootstrap.js';
    default:
      break;
  }
};

export const defaultPageHeader: PageHeader = {
  title: 'Virgin Atlantic',
  brandName: 'Virgin Atlantic',
  description: '',
  canonicalUrl: 'https://virginatlanic.com/',
  favicon: '/_nextfavicons/favicon.ico',
  faviconAppleTouch: '/_nextfavicons/apple-touch-icon.png',
  favicon16: '/_nextfavicons/favicon-16x16.png',
  favicon32: '/_nextfavicons/favicon-32x32.png',
  faviconSafariPinned: '/_nextfavicons/safari-pinned-tab.svg',
  appleTouchIconPrecomposed: '/_nextfavicons/apple-touch-icon-precomposed.png',
  preloadFiles: [],
  searchPanelConfig: {
    hideSearchPanel: true,
  },
  aemLastModifiedDate: new Date().toISOString(),
  translationsJS: {
    id: process.env.TRANSLATIONS_ID || '',
    url: process.env.TRANSLATIONS_URL || ''
  },
};

export function getErrorPageHeader(): PageHeader {
  return {
    ...defaultPageHeader,
    title: 'Something\'s gone wrong',
    description: 'We\'re working to fix this as soon as possible.',
    canonicalUrl: '',
    appleTouchIconPrecomposed: `${assetPath}/_nextfavicons/apple-touch-icon-precomposed.png`,
  };
}

export async function getPageHeader(): Promise<PageHeader> {
  return {
    ...defaultPageHeader,
    title: 'Something\'s gone wrong',
    description: 'We\'re working to fix this as soon as possible.',
    canonicalUrl: '',
    appleTouchIconPrecomposed: `${assetPath}/_nextfavicons/apple-touch-icon-precomposed.png`,
    preloadFiles: await getPreloadFiles(),
  };
}

export async function getPageHeaderData(
  pageTitle: string,
  description: string,
  canonicalUrl: string,
  searchPanelConfig: SearchPanelConfig,
  lastModifiedDate?: string
): Promise<PageHeader> {
  const pageHeader = await getPageHeader();

  pageHeader.title = pageTitle ? pageTitle : BRAND_NAMES.VAA;
  pageHeader.brandName = BRAND_NAMES.VAA;
  pageHeader.description = description ?? '';
  pageHeader.canonicalUrl = canonicalUrl ?? '';
  pageHeader.searchPanelConfig = searchPanelConfig;
  pageHeader.aemLastModifiedDate = lastModifiedDate ?? '';

  return pageHeader;
}

export const getPreloadFiles = async (): Promise<PreloadFile[]> => {
  const hostName = getHostName();
  try {
    const buildEndpoint = `${getHostName()}/next-header-footer/api/build`;
    const res = await fetch(buildEndpoint, { method: 'GET' });

    if (!res.ok) {
      // new Logger().error('Failed to fetch build info from: ' + buildEndpoint);
      return [];
    }

    const buildJson = await res.json();
    const assets = buildJson['buildInfo']['assets'] || {};
    const cssFiles = assets['css'] || [];
    const jsFiles = assets['js'] || [];
    const fontFiles = assets['fonts'] || [];

    const files = [cssFiles, jsFiles, fontFiles].flat();

    if (files.length > 0) {
      return files.map((file) => {
        return {
          ...file,
          path: `${hostName}${file.path}`,
        };
      });
    }
    return [];
  } catch (error) {
    new Logger().error('Error while fetching preload files: ' + error);
    return [];
  }
};
